import type { Auth, User } from "firebase/auth";
import { Fragment, type KeyboardEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayFromSearch } from "./driverRoutes";
import { readingTime } from "./format";
import { PlaceMark } from "./PlaceConfirm";
import { personName } from "./requests";
import { go, useTitle } from "./hooks";
import { saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { VisitTags } from "./VisitLabels";
import { type Failure, failureOf, Problem } from "./UserPages";
import {
  ACCOUNT_VISIT_COLUMNS,
  accountLabel,
  applyBatch,
  batchText,
  findAccount,
  gridBatch,
  type GridRow,
  ENTRY_KINDS,
  KIND_TEXT,
  localNow,
  nextRow,
  NOTHING_REASONS,
  outcomeText,
  REASON_TEXT,
  rowEntry,
  VISIT_COLUMNS,
  type Volume,
  volumeOf,
  volumeText,
} from "./visits";
import { Skeleton } from "./Skeleton";

const newId = () => crypto.randomUUID();
type Note = { ok: boolean; text: string } | null;

interface Choices {
  accounts: api.Account[];
  drivers: api.VisitChoice[];
  trucks: { id: string; code: string }[];
  volume: Volume;
}

const amountText = (gal: number | null, volume: Volume) => (gal == null ? "" : String(Math.round(gal * volume.perGal * 10) / 10));

/** A saved visit as a grid row, for a correction. */
function rowOf(visit: api.Visit, timeZone: string, volume: Volume): GridRow {
  const at = localNow(new Date(visit.at), timeZone);
  return {
    key: visit.id,
    id: newId(),
    account: `${visit.account_number} · ${visit.account_name}`,
    accountId: visit.account_id,
    day: at.day,
    time: at.time,
    driverId: visit.driver_id ?? "",
    truckId: visit.truck_id ?? "",
    raw: amountText(visit.raw_gal, volume),
    scrap: amountText(visit.scrap_gal, volume),
    left: amountText(visit.gallons_left, volume),
    kind: visit.kind,
    reason: visit.reason ?? "",
    state: "new",
    error: "",
  };
}

function RowCells({
  row,
  index,
  choices,
  locked,
  onChange,
  onEnter,
}: {
  row: GridRow;
  index: number;
  choices: Choices;
  locked: boolean;
  onChange: (fields: Partial<GridRow>) => void;
  onEnter: () => void;
}) {
  const enter = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    onEnter();
  };
  const label = (what: string) => `${what}, row ${index + 1}`;
  const reasons = row.kind === "level" ? (["not_full"] as const) : NOTHING_REASONS.filter((r) => r !== "not_full");
  return (
    <>
      <td>
        <input
          className="grid-account"
          data-testid="grid-account"
          data-row={index}
          list="visit-accounts"
          aria-label={label("Account")}
          value={row.account}
          disabled={locked}
          autoComplete="off"
          onKeyDown={enter}
          onChange={(e) => {
            const found = findAccount(e.target.value, choices.accounts);
            onChange({ account: e.target.value, accountId: found?.id ?? null });
          }}
          onBlur={() => {
            const found = findAccount(row.account, choices.accounts);
            if (found && row.account !== accountLabel(found)) onChange({ account: accountLabel(found), accountId: found.id });
          }}
        />
      </td>
      <td>
        <input type="date" className="grid-day" data-testid="grid-day" aria-label={label("Date")} value={row.day} disabled={locked} onKeyDown={enter} onChange={(e) => onChange({ day: e.target.value })} />
      </td>
      <td>
        <input type="time" className="grid-time" data-testid="grid-time" aria-label={label("Time")} value={row.time} disabled={locked} onKeyDown={enter} onChange={(e) => onChange({ time: e.target.value })} />
      </td>
      <td>
        <select className="grid-driver" data-testid="grid-driver" aria-label={label("Driver")} value={row.driverId} disabled={locked} onKeyDown={enter} onChange={(e) => onChange({ driverId: e.target.value })}>
          <option value="">–</option>
          {choices.drivers.map((driver) => (
            <option key={driver.id} value={driver.id}>
              {personName(driver.name)}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select className="grid-truck" data-testid="grid-truck" aria-label={label("Truck")} value={row.truckId} disabled={locked} onKeyDown={enter} onChange={(e) => onChange({ truckId: e.target.value })}>
          <option value="">–</option>
          {choices.trucks.map((truck) => (
            <option key={truck.id} value={truck.id}>
              {truck.code}
            </option>
          ))}
        </select>
      </td>
      {(["raw", "scrap", "left"] as const).map((field) => (
        <td key={field} className="num">
          <input
            data-testid={`grid-${field}`}
            inputMode="decimal"
            autoComplete="off"
            aria-label={label(field === "left" ? "Level left" : field === "raw" ? "Raw" : "Scrap")}
            value={row[field]}
            disabled={locked || (field !== "left" && row.kind !== "pickup")}
            onKeyDown={enter}
            onChange={(e) => onChange({ [field]: e.target.value })}
          />
        </td>
      ))}
      <td>
        <div className="grid-outcome">
        <select
          data-testid="grid-kind"
          aria-label={label("Outcome")}
          value={row.kind}
          disabled={locked}
          onKeyDown={enter}
          onChange={(e) => {
            const kind = e.target.value as api.VisitKind;
            onChange({ kind, reason: kind === "level" ? "not_full" : kind === "pickup" ? "" : row.reason === "not_full" ? "" : row.reason });
          }}
        >
          {ENTRY_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {KIND_TEXT[kind]}
            </option>
          ))}
        </select>
        {row.kind !== "pickup" && (
          <select
            data-testid="grid-reason"
            aria-label={label("Reason")}
            value={row.reason}
            disabled={locked}
            onKeyDown={enter}
            onChange={(e) => onChange({ reason: e.target.value as api.VisitReason | "" })}
          >
            {row.kind === "no_access" && <option value="">Why?</option>}
            {reasons.map((reason) => (
              <option key={reason} value={reason}>
                {REASON_TEXT[reason]}
              </option>
            ))}
          </select>
        )}
        </div>
      </td>
    </>
  );
}

const HEAD = ["Account", "Date", "Time", "Driver", "Truck", "Raw", "Scrap", "Level left", "Outcome"];
const NUM_HEADS = new Set(["Raw", "Scrap", "Level left"]);

/** The office types a day of driver tickets: one row for each visit, keyboard first. */
export function VisitsPage({
  base,
  org,
  auth,
  user,
  canView,
  canEdit,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
  canEdit: boolean;
}) {
  const today = localNow(new Date(), org.time_zone).day;
  const [found, setFound] = useState<Choices | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [rows, setRows] = useState<GridRow[]>(() => [nextRow(undefined, today, newId)]);
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(() => dayFromSearch(location.search) ?? today);
  const [driver, setDriver] = useState("");
  const [saved, setSaved] = useState<api.Visit[] | Failure | undefined>(undefined);
  const [reload, setReload] = useState(0);
  const [correcting, setCorrecting] = useState<{ visit: api.Visit; row: GridRow } | null>(null);
  const [sort, setSort] = useSort("sort", VISIT_COLUMNS);
  const grid = useRef<HTMLTableSectionElement>(null);
  useTitle(`Visits · ${org.name}`);

  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setFound(undefined);
    user
      .getIdToken()
      .then(async (token) => {
        const [defaults, accounts] = await Promise.all([api.visitDefaults(token), api.accounts(token)]);
        if (!live) return;
        setFound({ accounts, drivers: defaults.drivers ?? [], trucks: defaults.trucks ?? [], volume: volumeOf(defaults.unit) });
        const self = (defaults.drivers ?? []).some((driver) => driver.id === defaults.driver_id) ? (defaults.driver_id ?? "") : "";
        setRows((before) =>
          before.map((row) => (row.state === "new" && !row.driverId && !row.truckId ? { ...row, driverId: self, truckId: defaults.truck_id ?? "" } : row)),
        );
      })
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);

  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setSaved(undefined);
    user
      .getIdToken()
      .then((token) => api.visits(token, { day, driver }))
      .then((list) => live && setSaved(list.visits))
      .catch((e) => live && setSaved(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, day, driver, reload, canView]);

  if (canView === false || found === "hidden") {
    return (
      <main data-testid="visits-hidden">
        <h1 tabIndex={-1}>Visits</h1>
        <p>Your role does not record visits.</p>
      </main>
    );
  }

  const choices = typeof found === "object" ? found : null;
  const changeRow = (key: string, fields: Partial<GridRow>) =>
    setRows((all) => all.map((row) => (row.key === key ? { ...row, ...fields, state: row.state === "error" ? "new" : row.state, error: "" } : row)));
  const focusRow = (index: number) =>
    requestAnimationFrame(() => grid.current?.querySelector<HTMLInputElement>(`[data-testid="grid-account"][data-row="${index}"]`)?.focus());
  const enter = (index: number) => {
    if (index === rows.length - 1) setRows((all) => [...all, nextRow(all[all.length - 1], today, newId)]);
    focusRow(index + 1);
  };

  const saveAll = async () => {
    if (!choices || busy) return;
    const { entries, problems } = gridBatch(rows, org.time_zone, choices.volume);
    setNote(null);
    if (!entries.length) {
      setRows((all) => applyBatch(all, [], { saved: 0, unchanged: 0, errors: [] }, problems));
      setNote({ ok: false, text: Object.keys(problems).length ? "Fix the red rows, then save again." : "Nothing to save." });
      return;
    }
    setBusy(true);
    try {
      const result = await api.recordVisits(await user.getIdToken(), entries);
      setRows((all) => {
        const next = applyBatch(all, entries, result, problems);
        return next.some((row) => row.state !== "saved") ? next : [...next, nextRow(next[next.length - 1], today, newId)];
      });
      const bad = result.errors.length + Object.keys(problems).length;
      setNote({ ok: bad === 0, text: bad ? `${batchText(result)} Fix the red rows, then save again.` : batchText(result) });
      setReload((n) => n + 1);
    } catch (e) {
      setRows((all) => applyBatch(all, [], { saved: 0, unchanged: 0, errors: [] }, problems));
      setNote({ ok: false, text: `${saveFailure(e, "The visits").text}` });
    } finally {
      setBusy(false);
    }
  };

  const saveCorrection = async () => {
    if (!choices || !correcting || busy) return;
    const entry = rowEntry(correcting.row, org.time_zone, choices.volume);
    if (typeof entry === "string") {
      setCorrecting({ ...correcting, row: { ...correcting.row, state: "error", error: entry } });
      return;
    }
    setBusy(true);
    try {
      await api.correctVisit(await user.getIdToken(), correcting.visit.id, entry);
      setCorrecting(null);
      setNote({ ok: true, text: `Saved: the correction of the visit at ${correcting.visit.account_number}.` });
      setReload((n) => n + 1);
    } catch (e) {
      setCorrecting({ ...correcting, row: { ...correcting.row, state: "error", error: saveFailure(e, "The correction").text } });
    } finally {
      setBusy(false);
    }
  };

  const drivers = choices?.drivers ?? [];
  const volume = choices?.volume ?? volumeOf(undefined);
  const open = rows.filter((row) => row.state !== "saved").length;

  return (
    <main className="visits-page wide" data-testid="visits-page" data-today={today}>
      <h1 tabIndex={-1}>Visits</h1>
      {found === undefined ? (
        <Skeleton shape="table" columns={6} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          <section aria-labelledby="visits-enter-title">
            <h2 id="visits-enter-title">Enter visits</h2>
            <p className="sub">
              One row for each ticket, in {volume.label}. Enter goes to the next row; the date, the driver and the truck come down from the row above.
            </p>
            <datalist id="visit-accounts">
              {found.accounts.map((account) => (
                <option key={account.id} value={accountLabel(account)} />
              ))}
            </datalist>
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Visits to enter">
              <table className="list visit-grid" data-testid="visit-grid">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    {HEAD.map((head) => (
                      <th key={head} scope="col" className={NUM_HEADS.has(head) ? "num" : undefined}>
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody ref={grid}>
                  {rows.map((row, index) => (
                    <Fragment key={row.key}>
                      <tr className={row.state === "error" ? "grid-error" : row.state === "saved" ? "grid-saved" : undefined} data-testid="grid-row" data-state={row.state}>
                        <th scope="row">
                          {index + 1}
                          {row.state === "saved" && <span className="sub">Saved</span>}
                        </th>
                        <RowCells row={row} index={index} choices={found} locked={row.state === "saved"} onChange={(fields) => changeRow(row.key, fields)} onEnter={() => enter(index)} />
                      </tr>
                      {row.state === "error" && (
                        <tr className="grid-error grid-error-text" data-testid="grid-error">
                          <td colSpan={HEAD.length + 1} className="error">
                            <span className="grid-error-line">
                              Row {index + 1}: {row.error}
                            </span>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="actions">
              <button type="button" className="primary" data-testid="visits-save-all" disabled={busy || open === 0} onClick={() => void saveAll()}>
                {busy ? "Saving…" : "Save all"}
              </button>
              <button type="button" data-testid="visits-add-row" onClick={() => enter(rows.length - 1)}>
                Add a row
              </button>
            </p>
            <p role="status" data-testid="visits-status" className={note && !note.ok ? "error" : undefined}>
              {note?.text}
            </p>
          </section>
          <section aria-labelledby="visits-saved-title">
            <h2 id="visits-saved-title">Saved visits</h2>
            <form className="filters" onSubmit={(e) => e.preventDefault()}>
              <label>
                Day
                <input type="date" data-testid="visits-day" value={day} onChange={(e) => setDay(e.target.value)} />
              </label>
              <label>
                Driver
                <select data-testid="visits-driver" value={driver} onChange={(e) => setDriver(e.target.value)}>
                  <option value="">All drivers</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {personName(d.name)}
                    </option>
                  ))}
                </select>
              </label>
            </form>
            {correcting && (
              <div className="visit-correct" data-testid="visit-correct">
                <h3>Correct the visit at {correcting.visit.account_number}</h3>
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Correction">
                  <table className="list visit-grid">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        {HEAD.map((head) => (
                          <th key={head} scope="col" className={NUM_HEADS.has(head) ? "num" : undefined}>
                            {head}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className={correcting.row.state === "error" ? "grid-error" : undefined}>
                        <th scope="row">1</th>
                        <RowCells
                          row={correcting.row}
                          index={0}
                          choices={found}
                          locked={busy}
                          onChange={(fields) => setCorrecting({ ...correcting, row: { ...correcting.row, ...fields, state: "new", error: "" } })}
                          onEnter={() => void saveCorrection()}
                        />
                      </tr>
                    </tbody>
                  </table>
                </div>
                {correcting.row.error && (
                  <p role="alert" className="error" data-testid="visit-correct-error">
                    {correcting.row.error}
                  </p>
                )}
                <p className="actions">
                  <button type="button" className="primary" data-testid="visit-correct-save" disabled={busy} onClick={() => void saveCorrection()}>
                    Save the correction
                  </button>
                  <button type="button" onClick={() => setCorrecting(null)}>
                    Cancel
                  </button>
                </p>
              </div>
            )}
            {saved === undefined ? (
              <Skeleton shape="table" columns={4} rows={3} />
            ) : typeof saved === "string" ? (
              saved === "hidden" ? (
                <p>Your role does not see the saved visits.</p>
              ) : (
                <Problem found={saved} auth={auth} retry={() => setReload((n) => n + 1)} />
              )
            ) : !saved.length ? (
              <p data-testid="visits-empty">No visits on this day.</p>
            ) : (
              <>
                <SortSelect id="visits-select" columns={VISIT_COLUMNS} sort={sort} onSort={setSort} fallback="Oldest first" />
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Saved visits">
                  <table className="list visits-table" data-testid="visits-saved" data-cols="visits-select">
                    <thead>
                      <tr>
                        {VISIT_COLUMNS.map((column) => (
                          <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                        ))}
                        {canEdit && (
                          <th scope="col">
                            <span className="visually-hidden">Correct</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(saved, VISIT_COLUMNS, sort).map((visit) => (
                        <tr key={visit.id} className={visit.corrected ? "row-corrected" : undefined} data-testid="visit-row" data-kind={visit.kind}>
                          <th scope="row" title={readingTime(visit.at, org.time_zone)}>
                            {new Date(visit.at).toLocaleTimeString("en-US", { timeZone: org.time_zone, hour: "numeric", minute: "2-digit" })}
                          </th>
                          <td data-label="Account" className="wrap">
                            {visit.account_number}{" "}
                            <span className="sub clip short" title={visit.account_name}>
                              {visit.account_name}
                            </span>
                          </td>
                          <td data-label="Driver" className="visit-driver">
                            <span className="clip short" title={visit.driver_name ? personName(visit.driver_name) : undefined}>
                              {visit.driver_name ? personName(visit.driver_name) : "–"}
                            </span>
                          </td>
                          <td data-label="Truck">{visit.truck_name ?? "–"}</td>
                          <td data-label="Raw" className="num">
                            {volumeText(visit.raw_gal, volume)}
                          </td>
                          <td data-label="Scrap" className="num">
                            {volumeText(visit.scrap_gal, volume)}
                          </td>
                          <td data-label="Level left" className="num">
                            {volumeText(visit.gallons_left, volume)}
                          </td>
                          <td data-label="Outcome" className="wrap visit-outcome">
                            {outcomeText(visit)}
                            <PlaceMark place={visit.place} />
                            <VisitTags visit={visit} />
                          </td>
                          {canEdit && (
                            <td>
                              {!visit.corrected && visit.can_correct !== false && (
                                <button
                                  type="button"
                                  className="link"
                                  data-testid="visit-correct-open"
                                  aria-label={`Correct the visit at ${visit.account_number}`}
                                  onClick={() => setCorrecting({ visit, row: rowOf(visit, org.time_zone, volume) })}
                                >
                                  Correct
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
          {canEdit && (
            <p className="sub" data-testid="visits-parity-link">
              Sheet and app counts are on the{" "}
              <a href={`${base}/parity`} onClick={go}>
                Parity page
              </a>
              .
            </p>
          )}
        </>
      )}
    </main>
  );
}

/** The visits of one account, newest first: who came, with which truck, and the level that they left. */
export function AccountVisits({ id, org, user }: { id: string; org: api.Bootstrap; user: User }) {
  const [found, setFound] = useState<api.Visit[] | Failure | undefined>(undefined);
  const [sort, setSort] = useSort("visits", ACCOUNT_VISIT_COLUMNS);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.accountVisits(token, id))
      .then((list) => live && setFound(list.visits))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, id]);
  if (found === "hidden") return null;
  const volume = volumeOf(undefined);
  return (
    <section aria-labelledby="account-visits" data-testid="account-visits">
      <h2 id="account-visits">Visits</h2>
      {found === undefined ? (
        <Skeleton shape="table" columns={4} rows={3} />
      ) : typeof found === "string" ? (
        <p className="error">Cannot load the visits. Load the page again.</p>
      ) : !found.length ? (
        <p data-testid="account-no-visits">No visits recorded in the app yet.</p>
      ) : (
        <>
          <SortSelect id="account-visits-select" columns={ACCOUNT_VISIT_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Visits">
            <table className="list" data-testid="account-visit-rows" data-cols="account-visits-select">
              <thead>
                <tr>
                  {ACCOUNT_VISIT_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows([...found].sort((a, b) => b.at.localeCompare(a.at)), ACCOUNT_VISIT_COLUMNS, sort).map((visit) => (
                  <tr key={visit.id} className={visit.corrected ? "row-corrected" : undefined} data-testid="account-visit-row" data-kind={visit.kind}>
                    <th scope="row">{readingTime(visit.at, org.time_zone)}</th>
                    <td data-label="Visit">
                      {outcomeText(visit)}
                      {visit.kind === "pickup" && visit.raw_gal != null && <span className="sub">{volumeText(visit.raw_gal, volume)} taken</span>}
                      <PlaceMark place={visit.place} />
                      <VisitTags visit={visit} />
                    </td>
                    <td data-label="Driver">{visit.driver_name ? personName(visit.driver_name) : "–"}</td>
                    <td data-label="Truck">{visit.truck_name ?? "–"}</td>
                    <td className="num" data-label="Level left">{volumeText(visit.gallons_left, volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
